import { ChevronDown, Eye } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import type { PreviewTarget } from '../previewIdentity';

interface PreviewContributorBarProps {
  submitters: PreviewTarget[];
  /** The submitter currently being previewed, or null to offer the picker. */
  previewing: PreviewTarget | null;
  onSelect: (target: PreviewTarget) => void;
  onExit: () => void;
}

/**
 * AOZONE-115. Two modes in one component: the admin-dashboard entry affordance
 * (a submitter picker) and the active-preview banner.
 *
 * NAMING IS PART OF THE DELIVERABLE. This previews LAYOUT, not AUTHORIZATION —
 * the admin's cookie stays an admin cookie and every privileged route around
 * the preview stays privileged. Wording like "View as user" or "Impersonate"
 * would invite someone to treat this as a security control, which it is not.
 * Keep the copy in the preview-not-permission register; a test pins it.
 *
 * The banner is deliberately NOT sticky. TopBar (`top-0`), FilterBar
 * (`top-14`) and TeamView's own/all bar (`top-[6.5rem]`) form a hand-tuned
 * sticky stack; inserting a sticky element between them would require editing
 * those offsets, which changes the real CONTRIBUTOR view and breaks AOZONE-115
 * AC5. The "you cannot forget it is on" job is done by the page-level amber
 * frame in App.tsx instead.
 *
 * Active mode offers no submitter switcher: switching is Exit then re-pick.
 * That halves the interactive surface for no real loss.
 */
export function PreviewContributorBar({
  submitters,
  previewing,
  onSelect,
  onExit,
}: PreviewContributorBarProps) {
  // A blank submitterId cannot be previewed: `simulatedContributorMe` refuses
  // it (an empty slackUserId is the own/all bug, not a degraded identity).
  // Dropping such rows here keeps that refusal unreachable from the UI, so a
  // single malformed row costs one missing menu item rather than a failed
  // click. Paired with App's building the identity in `enterPreview` rather
  // than in a render body; a test pins this filter, so don't drop it silently.
  const offerable = submitters.filter((s) => s.submitterId.trim() !== '');

  if (previewing) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Contributor view preview"
        className="border-b border-amber-300 bg-amber-100 text-amber-950"
      >
        <div className="mx-auto flex max-w-[1280px] flex-wrap items-center gap-3 px-6 py-2 text-sm">
          <Eye aria-hidden className="h-4 w-4 shrink-0" />
          <span>
            <strong className="font-semibold">Preview</strong> — contributor view as{' '}
            <strong className="font-semibold">{previewing.submitterAlias}</strong>
          </span>
          {/* Says out loud what the scope limit is, where the person who could
              be misled by it will actually read it. */}
          <span className="text-amber-900/80">
            Layout preview only — you are still signed in as an admin and your
            permissions are unchanged.
          </span>
          <button
            type="button"
            onClick={onExit}
            className="ml-auto rounded-md border border-amber-400 bg-white/70 px-3 py-1 text-sm font-medium text-amber-950 transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            Exit preview
          </button>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Preview contributor view"
        disabled={offerable.length === 0}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:pointer-events-none disabled:opacity-50"
      >
        <Eye aria-hidden className="h-4 w-4 text-slate-400" />
        Preview contributor view
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel className="text-xs font-medium text-slate-500">
          Preview as…
        </DropdownMenuLabel>
        {/* Radio, and a specific submitter every time: the preview needs a
            simulated slackUserId for TeamView's own/all logic to mean anything,
            so there is deliberately no "everyone" option. */}
        <DropdownMenuRadioGroup
          value=""
          onValueChange={(id) => {
            const target = offerable.find((s) => s.submitterId === id);
            if (target) onSelect(target);
          }}
        >
          {offerable.map((s) => (
            <DropdownMenuRadioItem key={s.submitterId} value={s.submitterId}>
              {s.submitterAlias}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
